import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import CustomLoading from '../../util_components/custom_loading';
import { get_api_url } from '../../../utils/urls';
import { get_api, post_api } from '../../../redux/api_funcs';

// check_type → URL-friendly slug used by the standalone form page (?checkin=…).
const TYPE_TO_CHECKIN = {
  DAY_8: 'day8',
  DAY_15: 'day15',
  DAY_22: 'day22',
  WEEK_7: 'week7',
  WEEK_11: 'week11'
};

const STATUS_BADGE = {
  draft: { cls: 'bg-amber-50 text-amber-700', label: 'Draft' },
  submitted: { cls: 'bg-green-50 text-green-700', label: 'Completed' },
  pending: { cls: 'bg-gray-100 text-gray-600', label: 'Pending' }
};

const MEMBER_STATUS_BG = { Red: '#dc2626', Yellow: '#d97706', Green: '#16a34a' };

// Per-student early-retention checklist tasks (one row per checkpoint), shown in
// a popup from the Health pill on the student profile. Each row links to the
// standalone form page.
class ChecklistTasks extends Component {
  state = { items: [], loading: false, error: '', creating: {}, snapshotting: false };

  componentDidMount() {
    this.load();
  }

  // Instantly create a snapshot checklist for this member (bypasses generation
  // rules — auto data is frozen as of right now), then open its form page.
  create_instant = () => {
    this.setState({ snapshotting: true, error: '' });
    const url = get_api_url('/v2/early_retention_checklist/create_instant');
    post_api(url, { student_uuid: this.props.student_uuid }, true)
      .then((res) => {
        const uuid = res?.data?.data?.uuid;
        if (uuid) {
          window.open(
            `/early-retention-checklist-form?uuid=${encodeURIComponent(uuid)}`,
            '_blank',
            'noopener,noreferrer'
          );
        }
        this.load();
      })
      .catch((e) => {
        let err = 'Failed to generate snapshot';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.setState({ error: err });
      })
      .finally(() => this.setState({ snapshotting: false }));
  };

  // Create a checklist row for a not-yet-generated checkpoint, then refresh.
  create_task = (item) => {
    this.setState((prev) => ({ creating: { ...prev.creating, [item.check_type]: true } }));
    const url = get_api_url('/v2/early_retention_checklist/create_task');
    post_api(url, { student_uuid: this.props.student_uuid, check: item.check_type }, true)
      .then(() => this.load())
      .catch((e) => {
        let err = 'Failed to create task';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.setState({ error: err });
      })
      .finally(() =>
        this.setState((prev) => {
          const creating = { ...prev.creating };
          delete creating[item.check_type];
          return { creating };
        })
      );
  };

  load = () => {
    this.setState({ loading: true, error: '' });
    const url = get_api_url(
      `/v2/early_retention_checklist/student_tasks/${this.props.student_uuid}`
    );
    get_api(url, true)
      .then((res) => {
        this.setState({ items: res.data.data.items || [], loading: false });
      })
      .catch((e) => {
        let err = 'Failed to load checklist tasks';
        if (e.response?.data?.reason) err = e.response.data.reason;
        this.setState({ error: err, loading: false });
      });
  };

  // eslint-disable-next-line class-methods-use-this
  check_badge = (item) => {
    if (item.check_type === 'CUSTOM') {
      // Instant snapshots are CUSTOM rows created on demand from this popup.
      if (item.custom_rule_id === 'instant') {
        return (
          <span
            className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-50 text-teal-700"
            title={item.custom_name || 'Instant Snapshot'}
          >
            Instant
          </span>
        );
      }
      // Hover shows the full rule name ("Custom: <condition> + <filters>").
      return (
        <span
          className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-50 text-purple-700"
          title={item.custom_name || 'Custom'}
        >
          Custom
        </span>
      );
    }
    const text = item.week_number > 0 ? `Week ${item.week_number}` : `Day ${item.day_number}`;
    return (
      <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
        {text}
      </span>
    );
  };

  // eslint-disable-next-line class-methods-use-this
  status_badge = (status) => {
    const s = STATUS_BADGE[status] || STATUS_BADGE.pending;
    return (
      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${s.cls}`}>
        {s.label}
      </span>
    );
  };

  // eslint-disable-next-line class-methods-use-this
  member_status_badge = (status) => {
    const bg = MEMBER_STATUS_BG[status];
    if (!bg) return <span className="text-gray-400">—</span>;
    return (
      <span
        className="inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold text-white"
        style={{ background: bg }}
      >
        {status}
      </span>
    );
  };

  // Standard checkpoints are keyed by student + checkpoint; custom rows by
  // their uuid (a (student, checkin) pair doesn't identify them).
  form_link = (item) => {
    if (item.check_type === 'CUSTOM') {
      return `/early-retention-checklist-form?uuid=${encodeURIComponent(item.uuid)}`;
    }
    return `/early-retention-checklist-form?student_uuid=${encodeURIComponent(
      this.props.student_uuid
    )}&checkin=${TYPE_TO_CHECKIN[item.check_type] || ''}`;
  };

  render_row = (item) => {
    const creating = !!this.state.creating[item.check_type];
    return (
      <tr
        key={item.uuid || item.check_type}
        className="border-t border-line hover:bg-surface-sunken"
      >
        <td className="text-left px-4 py-2.5">{this.check_badge(item)}</td>
        <td className="text-left px-4 py-2.5 whitespace-nowrap text-content-secondary">
          {item.checkin_date || '—'}
        </td>
        <td className="text-left px-4 py-2.5">
          {item.exists ? (
            this.status_badge(item.status)
          ) : (
            <span className="inline-block px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
              Not created
            </span>
          )}
        </td>
        <td className="text-left px-4 py-2.5">{this.member_status_badge(item.overall_status)}</td>
        <td className="text-left px-4 py-2.5">
          {item.exists ? (
            <Link
              to={this.form_link(item)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-3 py-1.5 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
            >
              {item.status === 'submitted' ? 'View' : 'View form'}
            </Link>
          ) : (
            <button
              type="button"
              disabled={creating}
              onClick={() => this.create_task(item)}
              className="inline-block px-3 py-1.5 rounded-md text-xs font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-60 disabled:cursor-wait transition-colors"
            >
              {creating ? 'Creating…' : 'Create'}
            </button>
          )}
        </td>
      </tr>
    );
  };

  // "Generate snapshot now" — creates a checklist for this member as of right
  // now (no generation rule involved) and opens its form page in a new tab.
  render_snapshot_action = () => {
    const { snapshotting } = this.state;
    return (
      <div className="flex items-center justify-between gap-4 mb-3">
        <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
          Need a checklist for this member right now? Generate a snapshot — it skips the generation
          rules and freezes the data as of this moment. Takes about a minute — please wait.
        </div>
        <button
          type="button"
          disabled={snapshotting}
          onClick={this.create_instant}
          className="shrink-0 px-3 py-1.5 rounded-md text-xs font-medium text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-60 disabled:cursor-wait transition-colors"
        >
          {snapshotting ? 'Generating… (~1 min)' : 'Generate snapshot now'}
        </button>
      </div>
    );
  };

  render() {
    const { items, loading, error } = this.state;
    if (loading) return <CustomLoading />;

    let body;
    if (error) {
      body = (
        <div className="text-sm" style={{ color: 'var(--state-error-text, #b91c1c)' }}>
          {error}
        </div>
      );
    } else if (items.length === 0) {
      body = (
        <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
          No early-retention checklists for this member yet.
        </div>
      );
    } else {
      body = (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-sunken text-content-secondary">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Check</th>
                <th className="text-left px-4 py-2 font-medium">Checkin date</th>
                <th className="text-left px-4 py-2 font-medium">Checklist status</th>
                <th className="text-left px-4 py-2 font-medium">Member health</th>
                <th className="text-left px-4 py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>{items.map(this.render_row)}</tbody>
          </table>
        </div>
      );
    }

    return (
      <div>
        {this.render_snapshot_action()}
        {body}
      </div>
    );
  }
}

ChecklistTasks.propTypes = {
  student_uuid: PropTypes.string.isRequired
};

export default ChecklistTasks;
